import React, { type Dispatch, type SetStateAction, useEffect, useState } from "react";

import { useHistory } from "react-router-dom";
import { type InsightUserStatus } from "@doitintl/cmp-models";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

import { useApiContext } from "../../../api/context";
import { useErrorSnackbar } from "../../../Components/SharedSnackbar/SharedSnackbar.context";
import { queryKeys } from "../../../constants";
import { useAuthContext } from "../../../Context/AuthContext";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { consoleErrorWithSentry } from "../../../utils";
import { postInsightEnhancement } from "../api";
import { type Insight } from "../types";
import DismissInsightModal from "./DismissInsightModal";
import EnhanceInsightModal, { type InsightEnhancementData } from "./EnhanceInsightModal";

type Props = {
  selectedInsight: Insight | null;
  updateInsight: Dispatch<SetStateAction<Insight | null>>;
};

const defaultTags = [];

// TODO: Refactor this component to use the common component for the three dot menu
const InsightThreeDotMenu: React.FC<Props> = ({ selectedInsight, updateInsight }) => {
  const queryClient = useQueryClient();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { customer } = useCustomerContext();

  const history = useHistory();

  const open = Boolean(anchorEl);

  const api = useApiContext();

  const { userId, isDoitEmployee } = useAuthContext();
  const showErrorSnackbar = useErrorSnackbar();

  const [isDismissModalVisible, setIsDismissModalVisible] = useState(false);
  const [isEnhanceInsightModalVisible, setIsEnhanceInsightModalVisible] = useState(false);

  const insightStatus = selectedInsight?.userStatusChanges?.status;

  const isCustomInsight = selectedInsight?.providerId === "custom";
  const showMenu = isCustomInsight || (isDoitEmployee && insightStatus !== "dismissed");

  const [customInsightMenuOptions, setCustomInsightMenuOptions] = useState<{ label: string; status: string }[]>([]);
  const [automaticInsightMenuOptions, setAutomaticInsightMenuOptions] = useState<string[]>([]);

  useEffect(() => {
    if (!showMenu) {
      return;
    }

    const menuOptionOptimized = { label: "Mark as optimized", status: "optimized" };
    const menuOptionNotOptimized = { label: "Mark as actionable", status: "actionable" };
    const menuOptionDismiss = { label: "Dismiss", status: "dismissed" };
    const menuOptionEdit = { label: "Edit", status: "edit" };
    const shared = isDoitEmployee ? [menuOptionEdit] : [];

    if (isCustomInsight) {
      const newMenuOptions =
        insightStatus === "actionable"
          ? [menuOptionOptimized, menuOptionDismiss, ...shared]
          : [menuOptionNotOptimized, ...shared];

      setCustomInsightMenuOptions(newMenuOptions);
    } else if (isDoitEmployee) {
      setAutomaticInsightMenuOptions(["Enhance"]);
    }
  }, [insightStatus, isDoitEmployee, isCustomInsight, showMenu]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusUpdate = async (newStatus: string) => {
    handleClose();

    if (newStatus === "dismissed") {
      setIsDismissModalVisible(true);
    } else {
      const data = {
        customerId: customer.id,
        providerId: selectedInsight?.providerId,
        key: selectedInsight?.key,
        status: newStatus,
        userId,
      };

      try {
        await api.post("insights/status", data);

        // refresh the insights
        queryClient.invalidateQueries({ queryKey: [customer.id, queryKeys.insights] });

        updateInsight((insight) => {
          if (!insight) {
            return insight;
          }

          return {
            ...insight,
            userStatusChanges: insight.userStatusChanges && {
              ...insight.userStatusChanges,
              status: newStatus as InsightUserStatus,
            },
          };
        });
      } catch (e) {
        consoleErrorWithSentry(e);
        showErrorSnackbar("Something went wrong. Please try again later.");
      }
    }
  };

  const handleCustomInsightOptionClick = (status: string) => {
    if (status === "edit") {
      const newInsightUrl = `/customers/${customer.id}/optimize/insights/new-insight/${selectedInsight?.key}`;

      history.push(newInsightUrl);
    } else {
      handleStatusUpdate(status);
    }
  };

  const handleAutomaticInsightOptionClick = () => {
    setIsEnhanceInsightModalVisible(true);
  };

  const handleSaveInsightEnhancement = async (insightEnhancement: InsightEnhancementData) => {
    if (!selectedInsight) {
      return;
    }

    try {
      await postInsightEnhancement(api, {
        customerId: customer.id,
        providerId: selectedInsight.providerId,
        insightKey: selectedInsight.key,
        ...insightEnhancement,
      });

      updateInsight((insight) => {
        if (!insight) {
          return null;
        }
        return {
          ...insight,
          priority: insightEnhancement.priority || null,
          tags: insightEnhancement.tags,
          expertNote: insightEnhancement.expertNote
            ? {
                note: insightEnhancement.expertNote,
                createdAt: new Date().toISOString(),
              }
            : null,
        };
      });
    } catch (err) {
      consoleErrorWithSentry(err);
      showErrorSnackbar("Something went wrong. Please try again later.");
    }
  };

  if (!showMenu) {
    return null;
  }

  return (
    <>
      <IconButton
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        aria-label="actions"
        onClick={handleClick}
        size="medium"
        sx={{ mb: "-4px" }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu id="basic-menu" anchorEl={anchorEl} open={open} onClose={handleClose}>
        {
          // TODO: Refactor this to have a single menu items source
          isCustomInsight
            ? customInsightMenuOptions.map(({ label, status }) => (
                <MenuItem
                  key={status}
                  onClick={() => {
                    handleCustomInsightOptionClick(status);
                  }}
                >
                  {label}
                </MenuItem>
              ))
            : automaticInsightMenuOptions.map((label, index) => (
                <MenuItem key={`${label}-${index}`} onClick={handleAutomaticInsightOptionClick}>
                  {label}
                </MenuItem>
              ))
        }
      </Menu>
      {isDismissModalVisible && (
        <DismissInsightModal
          onClose={() => {
            setIsDismissModalVisible(false);
          }}
        />
      )}
      <EnhanceInsightModal
        existingPriority={selectedInsight?.priority ?? ""}
        existingTags={selectedInsight?.tags ?? defaultTags}
        existingExpertNote={selectedInsight?.expertNote?.note ?? ""}
        open={isEnhanceInsightModalVisible}
        onClose={() => {
          setIsEnhanceInsightModalVisible(false);
        }}
        onSave={handleSaveInsightEnhancement}
      />
    </>
  );
};

export default InsightThreeDotMenu;
